/**
 * @file 전체 게시판 — 게시판 여러 개의 최근 글을 카드로 한 화면에 모아 본다.
 *
 * <p>게시판을 하나씩 눌러 들어가면 "뭐가 새로 올라왔나" 를 알려면 5번을 왕복해야 한다.
 * 이 화면은 게시판들의 최근 글을 카드로 나란히 놓아 한 번에 훑게 한다.
 *
 * <h3>어떤 게시판을 보여줄지</h3>
 * <p><b>기본은 전체</b>다 — 아무 설정도 하지 않은 사람이 모든 게시판을 그대로 훑을 수 있어야 한다.
 * 거기서 '게시판 선택' 체크박스나 카드의 X 로 <b>빼는</b> 방식이고, 그 결과만 <b>localStorage</b> 에
 * 저장한다(사람마다 관심 게시판이 다르고, 서버에 둘 만큼 무거운 설정도 아니다).
 *
 * <h3>카드는 항상 같은 크기</h3>
 * <p>한 줄에 2장, 글이 있든 없든 같은 높이({@code ROWS_PER_CARD} 줄 분량)로 고정한다 — 글 수에 따라
 * 카드 키가 들쭉날쭉하면 여러 게시판을 훑는다는 이 화면의 목적이 흐려진다.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Box, Checkbox, Divider, FormControlLabel, IconButton, Paper, Popover, Stack, Tooltip, Typography,
} from "@mui/material";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ArrowDropUpRoundedIcon from "@mui/icons-material/ArrowDropUpRounded";
import ArrowDropDownRoundedIcon from "@mui/icons-material/ArrowDropDownRounded";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import ActionButton from "../../../component/common/ActionButton";
import LoadingDots from "../../../component/common/LoadingDots";
import { boardApi } from "../api/boardApi";
import { notify } from "../../../util/toast";
import {
    BOARD_HEADER_BG, C, NOTICE_ROW_BG, ROW_HOVER_SX, formatBoardDate,
} from "../boardShared";

const SELECTED_KEY = "board_home_selected_types";
const HIDE_NOTICE_KEY = "board_home_hide_notice";
/** 카드 한 장에 담는 글 수 — 시안과 같은 10줄. 카드 높이가 두 열 그리드에서 맞물리는 값. */
const ROWS_PER_CARD = 10;
/**
 * 글 한 줄 높이(px)와 그로부터 나오는 본문 영역 높이.
 *
 * <p>행 높이를 padding 이 아니라 {@code height} 로 못박는 이유: 카드 높이를 <b>글 수와 무관하게</b>
 * 고정해야 하기 때문이다. 10줄이 꽉 찬 카드와 "등록된 글이 없습니다" 만 있는 카드가 같은 크기여야
 * 두 열 그리드가 반듯하게 맞물린다.
 */
const ROW_H = 38;
const BODY_H = ROWS_PER_CARD * ROW_H;

function readStoredIds() {
    try {
        const raw = localStorage.getItem(SELECTED_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch { return null; }
}

export default function BoardHome({ types, onOpenBoard, onOpenPost }) {
    /** null = 저장된 선택값이 없음 → 전체를 보여준다(아래 visibleTypes). */
    const [selectedIds, setSelectedIds] = useState(() => readStoredIds());
    const [hideNotice, setHideNotice] = useState(() => {
        try { return localStorage.getItem(HIDE_NOTICE_KEY) === "1"; } catch { return false; }
    });
    const [pickerAnchor, setPickerAnchor] = useState(null);
    const [draftIds, setDraftIds] = useState([]);
    const [postsByType, setPostsByType] = useState({});
    const [loading, setLoading] = useState(true);

    /**
     * 보여줄 게시판 — 저장값이 없으면 전체, 있으면 저장값 ∩ 현재 존재하는 게시판.
     *
     * <p>교집합을 쓰는 이유: 저장 이후 게시판이 지워지거나 숨겨졌을 수 있다. 저장값을 그대로 믿으면
     * 사라진 게시판 id 로 조회해 <b>카드 하나가 통째로 에러</b>가 된다.
     */
    const visibleTypes = useMemo(() => {
        if (selectedIds == null) return types;
        const set = new Set(selectedIds.map(String));
        return types.filter(t => set.has(String(t.id)));
    }, [types, selectedIds]);

    const load = useCallback(async () => {
        const ids = visibleTypes.map(t => t.id);
        if (ids.length === 0) { setPostsByType({}); setLoading(false); return; }
        setLoading(true);
        try {
            const data = await boardApi.recent(ids, ROWS_PER_CARD);
            setPostsByType(data || {});
        } catch {
            notify("게시판 최근 글을 불러오지 못했습니다", "error");
            setPostsByType({});
        } finally {
            setLoading(false);
        }
        // visibleTypes 자체가 아니라 id 문자열에 의존 — 배열 identity 가 매번 새로 만들어져
        // 그대로 두면 매 렌더마다 재조회한다.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visibleTypes.map(t => t.id).join(",")]);

    useEffect(() => { load(); }, [load]);

    const persist = (ids) => {
        setSelectedIds(ids);
        try { localStorage.setItem(SELECTED_KEY, JSON.stringify(ids)); } catch { /* 사파리 프라이빗 모드 등 */ }
    };

    const toggleHideNotice = () => {
        setHideNotice(prev => {
            const next = !prev;
            try { localStorage.setItem(HIDE_NOTICE_KEY, next ? "1" : "0"); } catch { }
            return next;
        });
    };

    const openPicker = (e) => {
        setDraftIds(visibleTypes.map(t => t.id));
        setPickerAnchor(e.currentTarget);
    };

    /** 카드의 X — 그 게시판만 화면에서 뺀다(게시판 자체는 그대로). */
    const removeCard = (typeId) => {
        persist(visibleTypes.filter(t => t.id !== typeId).map(t => t.id));
    };

    const allChecked = draftIds.length === types.length && types.length > 0;

    // 좌우 여백은 카드가 pane 벽에 붙지 않을 만큼(32px), 최대 폭은 카드 두 장이 지나치게 넓어지지 않게.
    return (
        <Box sx={{ px: 4, py: 3, maxWidth: 1400, mx: "auto" }}>
            <Typography sx={{ fontSize: 20, fontWeight: 800, letterSpacing: "-0.3px", color: C.textPrimary, mb: 2 }}>
                전체 게시판
            </Typography>

            <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                <ActionButton
                    variant="secondary"
                    startIcon={false}
                    onClick={toggleHideNotice}
                    sx={{
                        fontSize: 13,
                        ...(hideNotice
                            ? { bgcolor: C.primaryBg, borderColor: C.primaryBorder, color: C.primary }
                            : {}),
                    }}
                    label={hideNotice ? "공지 보이기" : "공지 숨기기"} />
                <ActionButton
                    variant="secondary"
                    startIcon={false}
                    onClick={openPicker}
                    endIcon={pickerAnchor
                        ? <ArrowDropUpRoundedIcon sx={{ fontSize: 18 }} />
                        : <ArrowDropDownRoundedIcon sx={{ fontSize: 18 }} />}
                    sx={{ fontSize: 13 }}
                    label="게시판 선택" />
            </Stack>

            {/* 게시판 선택 — 전체 선택 + 개별 체크. '저장' 을 눌러야 반영된다(잘못 껐다가 되돌리기 쉽게). */}
            <Popover
                open={Boolean(pickerAnchor)}
                anchorEl={pickerAnchor}
                onClose={() => setPickerAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                slotProps={{ paper: { sx: { mt: 0.5, borderRadius: 1.5, minWidth: 220, boxShadow: "0 8px 24px rgba(15,23,42,0.14)" } } }}
            >
                <Box sx={{ p: 1.5, pb: 1 }}>
                    <FormControlLabel
                        control={
                            <Checkbox size="small" checked={allChecked}
                                indeterminate={draftIds.length > 0 && !allChecked}
                                onChange={e => setDraftIds(e.target.checked ? types.map(t => t.id) : [])} />
                        }
                        label={<Typography sx={{ fontSize: 13, fontWeight: 700 }}>전체 선택</Typography>}
                    />
                    <Stack>
                        {types.map(t => (
                            <FormControlLabel key={t.id}
                                control={
                                    <Checkbox size="small"
                                        checked={draftIds.includes(t.id)}
                                        onChange={e => setDraftIds(prev =>
                                            e.target.checked ? [...prev, t.id] : prev.filter(id => id !== t.id))} />
                                }
                                label={<Typography sx={{ fontSize: 13 }}>{t.name}</Typography>}
                            />
                        ))}
                    </Stack>
                    <Divider sx={{ my: 1 }} />
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <ActionButton variant="cancel" onClick={() => setPickerAnchor(null)}
                            sx={{ fontSize: 12.5, py: 0.3 }} />
                        <ActionButton variant="save" startIcon={false}
                            onClick={() => { persist(draftIds); setPickerAnchor(null); }}
                            sx={{ fontSize: 12.5, py: 0.3 }} />
                    </Stack>
                </Box>
            </Popover>

            {loading ? (
                <LoadingDots />
            ) : visibleTypes.length === 0 ? (
                <Paper elevation={0} sx={{
                    border: `1px solid ${C.borderLight}`, borderRadius: 1.5, bgcolor: C.surface,
                    py: 8, textAlign: "center",
                }}>
                    <Typography sx={{ fontSize: 14, color: C.textSecondary, fontWeight: 600, mb: 0.5 }}>
                        표시할 게시판이 없습니다
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, color: C.textMuted }}>
                        '게시판 선택' 에서 보고 싶은 게시판을 고르세요.
                    </Typography>
                </Paper>
            ) : (
                /*
                 * 한 줄에 2장 고정.
                 *
                 * <p>MUI Grid 를 쓰지 않는다 — v7 의 Grid 는 {@code item}/{@code xs}/{@code lg} 를 더 이상
                 * 받지 않아(신 Grid 는 {@code size={{...}}}) 예전 코드가 <b>조용히 무시</b>되고 카드가
                 * 폭 없이 한 줄에 전부 늘어섰다. CSS grid 는 그런 조용한 실패가 없고 "2등분" 을 그대로 쓴다.
                 */
                <Box sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
                    gap: 2.5,
                }}>
                    {visibleTypes.map(type => (
                        <BoardCard
                            key={type.id}
                            type={type}
                            posts={filterRows(postsByType[type.id], hideNotice)}
                            onOpenBoard={() => onOpenBoard(type.id)}
                            onOpenPost={onOpenPost}
                            onRemove={() => removeCard(type.id)}
                        />
                    ))}
                </Box>
            )}
        </Box>
    );
}

/** '공지 숨기기' 는 고정 공지 행만 걷어낸다 — 일반 글은 그대로 남는다. */
function filterRows(rows, hideNotice) {
    const list = Array.isArray(rows) ? rows : [];
    return hideNotice ? list.filter(r => !r.notice) : list;
}

/** 게시판 카드 한 장 — 제목 줄 + 글 목록. 행 사이 가로선은 두지 않는다. */
function BoardCard({ type, posts, onOpenBoard, onOpenPost, onRemove }) {
    return (
        <Paper elevation={0} sx={{
            border: `1px solid ${C.border}`, borderRadius: 1.5,
            overflow: "hidden", bgcolor: C.surface,
            display: "flex", flexDirection: "column",
        }}>
            {/*
              제목 줄 — 누르면 그 게시판으로 들어간다.
              색은 글 목록의 표 헤더({@code BOARD_HEADER_BG})와 <b>같은 값</b>을 쓴다. 이 카드의 제목 줄과
              목록 화면의 헤더는 사용자가 곧바로 이어서 보는 두 면이라, 톤이 어긋나면 다른 화면처럼 읽힌다.
            */}
            <Stack direction="row" alignItems="center" sx={{ bgcolor: BOARD_HEADER_BG, px: 2, py: 1.35 }}>
                <Stack direction="row" alignItems="center" spacing={0.5}
                    onClick={onOpenBoard}
                    sx={{ cursor: "pointer", flex: 1, minWidth: 0, "&:hover": { opacity: 0.85 } }}>
                    <Typography noWrap sx={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>
                        {type.name}
                    </Typography>
                    <ArrowForwardIosRoundedIcon sx={{ fontSize: 11, color: "rgba(255,255,255,0.8)" }} />
                </Stack>
                <Tooltip title="이 카드 숨기기">
                    <IconButton size="small" onClick={onRemove} sx={{ p: 0.3 }}>
                        <CloseRoundedIcon sx={{ fontSize: 17, color: "rgba(255,255,255,0.85)" }} />
                    </IconButton>
                </Tooltip>
            </Stack>

            {/* 높이 고정 — 글이 0개든 10개든 카드 크기가 같아야 두 열이 반듯하게 맞물린다. */}
            <Box sx={{ height: BODY_H, overflow: "hidden" }}>
                {posts.length === 0 ? (
                    <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Typography sx={{ fontSize: 12.5, color: C.textMuted }}>
                            등록된 글이 없습니다
                        </Typography>
                    </Box>
                ) : posts.map(p => (
                    <Stack key={p.id} direction="row" alignItems="center" spacing={1}
                        onClick={() => onOpenPost(type.id, p.id)}
                        sx={{
                            px: 2, height: ROW_H,
                            ...ROW_HOVER_SX,
                            // 공지 행 — 옅은 브랜드 배경 + 굵은 제목. hover 는 한 단계 더 진하게.
                            ...(p.notice ? { bgcolor: NOTICE_ROW_BG, "&:hover": { bgcolor: C.primaryLight } } : {}),
                        }}>
                        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
                            <Typography noWrap sx={{
                                fontSize: 13, minWidth: 0,
                                fontWeight: p.notice ? 700 : 500,
                                color: p.notice ? C.textPrimary : "#334155",
                            }}>
                                {p.reply ? "└ " : ""}{p.title}
                            </Typography>
                            {p.attachmentCount > 0 && (
                                <AttachFileRoundedIcon sx={{ fontSize: 13, color: C.textMuted, flexShrink: 0 }} />
                            )}
                            {p.commentCount > 0 && (
                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#ea580c", flexShrink: 0 }}>
                                    [{p.commentCount}]
                                </Typography>
                            )}
                        </Stack>
                        <Typography noWrap sx={{
                            fontSize: 12, width: 78, textAlign: "right", flexShrink: 0,
                            fontWeight: p.notice ? 700 : 400,
                            color: p.notice ? C.textPrimary : C.textSecondary,
                        }}>
                            {p.authorName || "—"}
                        </Typography>
                        <Typography sx={{
                            fontSize: 12, width: 62, textAlign: "right", flexShrink: 0,
                            fontWeight: p.notice ? 700 : 400,
                            color: p.notice ? C.textPrimary : C.textMuted,
                        }}>
                            {formatBoardDate(p.createdAt)}
                        </Typography>
                    </Stack>
                ))}
            </Box>
        </Paper>
    );
}
